// infrastructure/mappers/staffQualificationsMapper.js
import { createCredential, nextCredentialExpiry } from '../../domain/model/Credential';
import { createDegree } from '../../domain/model/Degree';

/**
 * The boundary between a person's stored document and the qualifications this context reads it as.
 *
 * TWO DIRECTIONS, like `providerMapper` one context over and for the same reason. The record is a
 * `clients` document carrying `type: 'staff'`, owned by the staff editor rather than by us, so this
 * reads it and hands back a PATCH the host merges into the draft it is already saving. A second
 * writer against a record being edited would have its work put back by the outer save.
 *
 * THE PATCH IS NARROW ON PURPOSE. It names only the fields this context owns, because that document
 * has several owners and a shape built for one of them carries that one's idea of the record into a
 * save meant to change something else.
 *
 * A STORED TIMESTAMP IS READ, BUT NEVER WRITTEN. Expiry dates recorded before this context existed
 * are Firestore Timestamps, and an instant is not a calendar day: reading one needs a timezone, and
 * `toDateStr` in revenueCycle refuses the conversion outright for exactly that reason. The situation
 * here is different, because this is legacy data that already exists rather than an input somebody
 * is authoring, so it is read in UTC and the error direction is deliberate: for a clinic east of UTC
 * the day can land one EARLIER, never one later, so a credential can read as lapsed a day early and
 * never as valid a day late.
 *
 * That conversion also cannot reach a claim on its own. A legacy row carries no start date, so it
 * resolves to nothing whatever its expiry says, and the first edit through the new editor writes
 * both dates back as calendar strings. The Timestamp form dies out as records are touched.
 */

/** A Firestore Timestamp, a Date, or a string, as a calendar day. See the note above on UTC. */
const toCalendarDay = (value) => {
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }

  const instant = typeof value?.toDate === 'function' ? value.toDate() : value;

  return instant instanceof Date && !Number.isNaN(instant.getTime())
    ? instant.toISOString().slice(0, 10)
    : null;
};

/**
 * What the stored entry held that this context does not own, carried on the record it was read from.
 *
 * THE PATCH REPLACES THE WHOLE ARRAY, so a field left out of it is a field deleted. These documents
 * have other owners: `taxonomy` on an `nppesNpi` row is the clearest case, deliberately kept
 * readable by the deprecation in `credentialingBodies` and read by four of the staff tables. Without
 * this, the first edit anybody made to any credential would take it with them.
 *
 * A SYMBOL RATHER THAN A FIELD, because this is not part of the model: the domain may not read it,
 * `Object.keys` does not see it, and so it cannot reach Firestore through a spread that was only
 * meant to copy the record. It does survive a deliberate spread, which is what carries these fields
 * through an EDIT — the dialog opens on `{ ...credential }` — rather than only through an untouched
 * save. A renewal builds from named fields instead and so carries none of it, which is right: that
 * is a different certificate, not the same one restated.
 */
const UNOWNED = Symbol('unownedStoredFields');

/**
 * Whatever the stored entry held under a key the model has no slot for.
 *
 * Membership is tested against the MAPPED record rather than a list of field names, because the
 * factories always return their whole shape — every key present, absent values as explicit nulls —
 * so `key in mapped` is the model's own answer and cannot drift from it.
 */
const carryUnowned = (mapped, entry) => {
  const unowned = Object.entries(entry ?? {}).filter(([key]) => !(key in mapped));

  return unowned.length === 0
    ? mapped
    : Object.assign(mapped, { [UNOWNED]: Object.fromEntries(unowned) });
};

/**
 * Reads a stored staff document as the qualifications this context owns.
 *
 * Every list reads as empty rather than absent when the document holds nothing, because "no degrees
 * recorded" and "this person has no degrees" are the same thing to every reader, and an absent list
 * would make each caller invent the empty case.
 *
 * @param {Object|null|undefined} doc - a `clients` document with `type: 'staff'`
 */
export function toStaffQualifications(doc) {
  if (doc === null || doc === undefined) {
    return null;
  }

  return {
    staffId: doc.id ?? null,
    clinicId: doc.clinic ?? null,
    degrees: (doc.degrees ?? [])
      .map((entry) => {
        const degree = createDegree({ ...entry, conferredOn: toCalendarDay(entry?.conferredOn) });

        return degree === null ? null : carryUnowned(degree, entry);
      })
      .filter((degree) => degree !== null),
    credentials: (doc.credentials ?? [])
      .map((entry) => {
        const credential = createCredential({
          ...entry,
          startDate: toCalendarDay(entry?.startDate),
          expirationDate: toCalendarDay(entry?.expirationDate),
        });

        return credential === null ? null : carryUnowned(credential, entry);
      })
      .filter((credential) => credential !== null),
  };
} // toStaffQualifications()

/**
 * Absent, meaning either form of it.
 *
 * Firestore refuses `undefined` outright, and the two arrive by different routes: a record READ from
 * the document carries an explicit null for a field nobody filled, while one built fresh by a dialog
 * simply has no such key. Checking only for null let the second through and the save failed on a
 * value the form never showed anybody.
 */
const isAbsent = (value) => value === null || value === undefined;

/**
 * The patch the staff editor merges into the draft it already holds.
 *
 * Dates go back as calendar strings, never as Timestamps, which is how the legacy form is retired
 * one record at a time. `status` is deliberately absent: it is derived from the window now, so a
 * value written here would be a second answer to a question the dates already settle.
 *
 * WHAT THIS CONTEXT DOES NOT OWN GOES BACK UNDERNEATH, never over the top — see `UNOWNED`. The patch
 * replaces the array whole, so a stored field nobody here understands survives only by being written
 * again, and it may never win over a value the model does own.
 */
export function toQualificationsPatch({ degrees, credentials }) {
  return {
    degrees: (degrees ?? []).map((degree) => ({
      ...degree[UNOWNED],
      level: degree.level,
      fieldOfStudy: degree.fieldOfStudy,
      school: degree.school,
      conferredOn: degree.conferredOn,
      isAccredited: degree.isAccredited === true,
      ...(isAbsent(degree.equivalency) ? {} : { equivalency: degree.equivalency }),
      proof: degree.proof,
    })),
    // Maintained here because this is the only path that writes a credential, so the field cannot
    // drift from the list it summarizes. Firestore cannot range-query inside an array of maps, which
    // is the whole reason it is stored beside it rather than derived on read.
    nextCredentialExpiry: nextCredentialExpiry(credentials ?? []),
    credentials: (credentials ?? []).map((credential) => ({
      ...credential[UNOWNED],
      organization: credential.organization,
      level: credential.level,
      certificateId: credential.certificateId,
      jurisdiction: credential.jurisdiction,
      // Written again, unlike before. This is the issuer's standing, not the derived "expired" the
      // old field also carried — see the model for why the two were worth separating.
      status: credential.status,
      startDate: credential.startDate,
      expirationDate: credential.expirationDate,
      ...(isAbsent(credential.verification) ? {} : { verification: credential.verification }),
    })),
  };
} // toQualificationsPatch()
